import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Box, Chip, Typography } from "@mui/material";
import React, { FC } from "react";

import { HelpState, isWatched, progressOf } from "../helpStorage";
import { formatClock } from "../helpUtils";
import { HelpVideo } from "../types";

interface VideoCardProps {
  video: HelpVideo;
  state: HelpState;
  /** The "New on this page" card: a subtle primary border. */
  highlight?: boolean;
  onPlay: (start?: number) => void;
}

const VideoCard: FC<VideoCardProps> = ({ video, state, highlight = false, onPlay }) => {
  const position = progressOf(video, state);
  const watched = isWatched(video, state);
  const duration = video.durationSec;

  return (
    <Box
      sx={{
        border: 1,
        borderColor: highlight ? "primary.main" : "divider",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-label={`Play ${video.title}`}
        onClick={() => onPlay(watched ? 0 : position)}
        onKeyDown={(event) => event.key === "Enter" && onPlay(watched ? 0 : position)}
        sx={{ position: "relative", aspectRatio: "16 / 9", bgcolor: "action.hover", cursor: "pointer" }}
      >
        <Box
          component="img"
          src={video.posterUrl}
          alt=""
          sx={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }}
        />
        <Box sx={{ position: "absolute", top: 0, bottom: 0, left: "77%", my: "auto", ml: "-22px", width: 44, height: 44, borderRadius: "50%", bgcolor: "primary.main", display: "grid", placeItems: "center" }}>
          <PlayArrowIcon sx={{ color: "primary.contrastText" }} />
        </Box>
        {duration !== undefined && (
          <Typography
            variant="caption"
            sx={{ position: "absolute", right: 10, bottom: 8, color: "common.black", fontSize: 14, fontWeight: 600 }}
          >
            {formatClock(duration)}
          </Typography>
        )}
        {position > 0 && duration !== undefined && (
          <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 3, bgcolor: "divider" }}>
            <Box sx={{ height: "100%", width: `${Math.min(100, (position / duration) * 100)}%`, bgcolor: "primary.main" }} />
          </Box>
        )}
      </Box>
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography fontWeight={700} onClick={() => onPlay(watched ? 0 : position)} sx={{ lineHeight: 1.4, cursor: "pointer" }}>
          {video.title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.6, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
        >
          {video.description}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {video.modules.join(" · ")}
          </Typography>
          {highlight && !watched && <Chip size="small" color="primary" label="NEW" sx={{ height: 20 }} />}
        </Box>
      </Box>
    </Box>
  );
};

export default VideoCard;
